import { useCallback, useEffect, useState } from "react";
import DashboardShell from "@/components/layout/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayoutDashboard, Wheat, Store, ShoppingCart, PackagePlus, Wallet, BarChart3,
  Plus, Pencil, Archive, ArchiveRestore, Power, Trash2, BadgeCheck,
} from "lucide-react";
import { TabBtn } from "@/components/transport/transportShared";
import {
  FeedBadge, SellerBadge, FeedTable, FeedFormDialog, FF, OrdersPanel, fmtMoney,
} from "@/components/feed/feedShared";

/* ---------- DASHBOARD TAB ---------- */
function DashboardTab() {
  const [kpi, setKpi] = useState(null);
  const [fin, setFin] = useState(null);
  useEffect(() => {
    api.get("/feed/dashboard").then((r) => setKpi(r.data)).catch(() => {});
    api.get("/feed/finance/summary").then((r) => setFin(r.data)).catch(() => {});
  }, []);
  if (!kpi) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const cards = [
    ["AnimalMela Products", kpi.direct_products],
    ["Marketplace Products", kpi.marketplace_products],
    ["Low Stock Alerts", kpi.low_stock_count],
    ["Today's Orders", `${kpi.today_orders.count} · ${fmtMoney(kpi.today_orders.amount)}`],
    ["Unpaid Orders", `${kpi.unpaid_orders.count} · ${fmtMoney(kpi.unpaid_orders.amount)}`],
    ["Pending Settlements", kpi.pending_settlements],
    ...(fin ? [
      ["Direct Gross Margin", fmtMoney(fin.direct.gross_margin)],
      ["Commission Earned", fmtMoney(fin.marketplace.commission_earned)],
      ["Own Stock Value", fmtMoney(fin.direct.stock_value)],
    ] : []),
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="feed-kpi-grid">
      {cards.map(([label, value]) => (
        <div key={label} className="am-card p-4" data-testid={`feed-kpi-${label.toLowerCase().replace(/[' ]/g, "-")}`}>
          <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
          <div className="font-display text-xl font-extrabold mt-1">{value}</div>
        </div>
      ))}
    </div>
  );
}

/* ---------- PRODUCTS TAB ---------- */
function ProductsTab({ products, suppliers, refresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [stockFor, setStockFor] = useState(null); // product for stock update
  const [stockVal, setStockVal] = useState("");
  const [busy, setBusy] = useState(false);
  const mkSuppliers = suppliers.filter((s) => !s.is_archived && s.supplier_type !== "PROCUREMENT");

  const openAdd = () => { setEditing(null); setForm({ supply_model: "DIRECT", category: "chara", unit: "quintal" }); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm(p); setOpen(true); };
  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        const { name, category, unit, selling_price, commission_percent } = form;
        await api.patch(`/feed/products/${editing.product_id}`, {
          name, category, unit, selling_price: Number(selling_price),
          commission_percent: commission_percent === "" || commission_percent == null ? undefined : Number(commission_percent),
        });
      } else {
        await api.post("/feed/products", {
          ...form, selling_price: Number(form.selling_price || 0),
          supplier_stock: Number(form.supplier_stock || 0),
          commission_percent: form.commission_percent ? Number(form.commission_percent) : undefined,
        });
      }
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const saveStock = () => act(async () => {
    if (stockFor.supply_model === "MARKETPLACE") {
      await api.post(`/feed/products/${stockFor.product_id}/set-stock`, { qty: Number(stockVal) });
    } else {
      await api.post(`/feed/products/${stockFor.product_id}/adjust-stock`, { qty_change: Number(stockVal), note: "manual adjustment" });
    }
    setStockFor(null);
  }, "Stock updated");

  return (
    <div className="space-y-4">
      <Button className="am-btn-primary rounded-full" data-testid="feed-add-product-btn" onClick={openAdd}><Plus className="w-4 h-4 mr-1" /> Add Product</Button>
      <FeedTable testId="feed-products-table" cols={["Product", "Seller", "Price", "Stock", "Avg Cost", "Commission", "Actions"]}>
        {products.map((p) => (
          <tr key={p.product_id} className={`border-b last:border-0 ${p.is_archived ? "opacity-50" : ""}`} data-testid={`feed-product-row-${p.product_id}`}>
            <td className="px-3 py-3 whitespace-nowrap font-bold">{p.name}
              <div className="text-xs font-normal text-muted-foreground">{p.category} · per {p.unit}{p.is_archived ? " · archived" : !p.is_active ? " · inactive" : ""}</div>
            </td>
            <td className="px-3 py-3"><SellerBadge model={p.supply_model} seller={p.seller_name} /></td>
            <td className="px-3 py-3 whitespace-nowrap font-bold">{fmtMoney(p.selling_price)}</td>
            <td className="px-3 py-3 whitespace-nowrap" data-testid={`feed-stock-${p.product_id}`}>
              {p.available_stock} {p.unit}
              {p.supply_model === "DIRECT" && p.available_stock <= 10 && <span className="ml-1.5 text-[10px] font-bold text-destructive">LOW</span>}
            </td>
            <td className="px-3 py-3 whitespace-nowrap">{p.supply_model === "DIRECT" ? fmtMoney(p.avg_cost) : "—"}</td>
            <td className="px-3 py-3 whitespace-nowrap">{p.supply_model === "MARKETPLACE" ? `${p.commission_percent ?? suppliers.find((s) => s.supplier_id === p.supplier_id)?.commission_percent ?? 10}%` : "—"}</td>
            <td className="px-3 py-3">
              <div className="flex gap-1 justify-end flex-wrap">
                <Button size="icon" variant="ghost" title="Edit" data-testid={`feed-edit-${p.product_id}`} onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" title={p.supply_model === "MARKETPLACE" ? "Set supplier stock" : "Adjust stock"}
                  data-testid={`feed-stockbtn-${p.product_id}`} onClick={() => { setStockFor(p); setStockVal(""); }}>
                  <PackagePlus className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" title={p.is_active ? "Deactivate" : "Activate"} data-testid={`feed-toggle-${p.product_id}`}
                  onClick={() => act(() => api.patch(`/feed/products/${p.product_id}`, { is_active: !p.is_active }), p.is_active ? "Deactivated" : "Activated")}>
                  <Power className={`w-4 h-4 ${p.is_active ? "text-emerald-600" : "text-muted-foreground"}`} />
                </Button>
                {!p.is_archived
                  ? <Button size="icon" variant="ghost" title="Archive" data-testid={`feed-archive-${p.product_id}`} onClick={() => act(() => api.post(`/feed/products/${p.product_id}/archive`), "Archived")}><Archive className="w-4 h-4 text-destructive" /></Button>
                  : <Button size="icon" variant="ghost" title="Restore" data-testid={`feed-restore-${p.product_id}`} onClick={() => act(() => api.post(`/feed/products/${p.product_id}/restore`), "Restored")}><ArchiveRestore className="w-4 h-4 text-primary" /></Button>}
              </div>
            </td>
          </tr>
        ))}
      </FeedTable>

      <FeedFormDialog open={open} setOpen={setOpen} busy={busy} onSave={save} title={editing ? "Edit Product" : "Add Product"}
        fields={<>
          <FF label="Product Name"><Input className="am-input" data-testid="fp-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FF>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Category"><Input className="am-input" data-testid="fp-category" placeholder="bhusa / chara / dana" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} /></FF>
            <FF label="Unit"><Input className="am-input" data-testid="fp-unit" placeholder="kg / quintal / bag" value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></FF>
          </div>
          {!editing && (
            <FF label="Business Model">
              <Select value={form.supply_model} onValueChange={(v) => setForm({ ...form, supply_model: v })}>
                <SelectTrigger className="am-input" data-testid="fp-model"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DIRECT">AnimalMela Direct Supply (own stock)</SelectItem>
                  <SelectItem value="MARKETPLACE">Third-Party Supplier (commission)</SelectItem>
                </SelectContent>
              </Select>
            </FF>
          )}
          {!editing && form.supply_model === "MARKETPLACE" && (
            <>
              <FF label="Supplier">
                <Select value={form.supplier_id || ""} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger className="am-input" data-testid="fp-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{mkSuppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name} ({s.commission_percent}%)</SelectItem>)}</SelectContent>
                </Select>
              </FF>
              <div className="grid grid-cols-2 gap-3">
                <FF label="Supplier Stock"><Input className="am-input" type="number" min={0} data-testid="fp-sstock" value={form.supplier_stock || ""} onChange={(e) => setForm({ ...form, supplier_stock: e.target.value })} /></FF>
                <FF label="Commission % (override)"><Input className="am-input" type="number" min={0} max={100} data-testid="fp-commission" placeholder="supplier default" value={form.commission_percent ?? ""} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} /></FF>
              </div>
            </>
          )}
          <FF label="Selling Price ₹ (per unit)"><Input className="am-input" type="number" min={0} data-testid="fp-price" value={form.selling_price ?? ""} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} /></FF>
        </>} />

      <FeedFormDialog open={!!stockFor} setOpen={() => setStockFor(null)} busy={busy} onSave={saveStock}
        title={stockFor?.supply_model === "MARKETPLACE" ? `Supplier stock — ${stockFor?.name}` : `Adjust AnimalMela stock — ${stockFor?.name}`}
        fields={
          <FF label={stockFor?.supply_model === "MARKETPLACE" ? `New total stock (current: ${stockFor?.supplier_stock})` : `Change ± (current: ${stockFor?.stock_qty})`}>
            <Input className="am-input" type="number" data-testid="feed-stock-input" value={stockVal} onChange={(e) => setStockVal(e.target.value)} />
          </FF>
        } />
    </div>
  );
}

/* ---------- SUPPLIERS TAB ---------- */
function SuppliersTab({ suppliers, refresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const openAdd = () => { setEditing(null); setForm({ supplier_type: "BOTH", commission_percent: 10 }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s, areas_text: (s.areas || []).join(", ") }); setOpen(true); };
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: form.name, business_name: form.business_name || "", mobile: form.mobile,
        supplier_type: form.supplier_type, payment_details: form.payment_details || "",
        commission_percent: Number(form.commission_percent ?? 10),
        areas: (form.areas_text || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (editing) await api.patch(`/feed/suppliers/${editing.supplier_id}`, body);
      else await api.post("/feed/suppliers", body);
      toast.success(editing ? "Supplier updated" : "Supplier added");
      setOpen(false); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return (
    <div className="space-y-4">
      <Button className="am-btn-primary rounded-full" data-testid="feed-add-supplier-btn" onClick={openAdd}><Plus className="w-4 h-4 mr-1" /> Add Supplier</Button>
      <FeedTable testId="feed-suppliers-table" cols={["Supplier", "Mobile", "Type", "Commission", "Areas", "Actions"]}>
        {suppliers.map((s) => (
          <tr key={s.supplier_id} className={`border-b last:border-0 ${s.is_archived ? "opacity-50" : ""}`} data-testid={`feed-supplier-row-${s.supplier_id}`}>
            <td className="px-3 py-3 font-bold whitespace-nowrap">{s.name}
              <div className="text-xs font-normal text-muted-foreground">{s.business_name || ""}{s.is_archived ? " · archived" : !s.is_active ? " · inactive" : ""}</div>
            </td>
            <td className="px-3 py-3 font-mono text-xs">{s.mobile}</td>
            <td className="px-3 py-3 text-xs font-semibold">{s.supplier_type}</td>
            <td className="px-3 py-3">{s.commission_percent}%</td>
            <td className="px-3 py-3 text-xs">{(s.areas || []).join(", ") || "—"}</td>
            <td className="px-3 py-3">
              <div className="flex gap-1 justify-end">
                <Button size="icon" variant="ghost" title="Edit" data-testid={`feed-sup-edit-${s.supplier_id}`} onClick={() => openEdit(s)}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" title={s.is_active ? "Deactivate" : "Activate"}
                  onClick={() => act(() => api.patch(`/feed/suppliers/${s.supplier_id}`, { is_active: !s.is_active }), s.is_active ? "Deactivated" : "Activated")}>
                  <Power className={`w-4 h-4 ${s.is_active ? "text-emerald-600" : "text-muted-foreground"}`} />
                </Button>
                {!s.is_archived
                  ? <Button size="icon" variant="ghost" title="Archive" data-testid={`feed-sup-archive-${s.supplier_id}`} onClick={() => act(() => api.post(`/feed/suppliers/${s.supplier_id}/archive`), "Archived")}><Archive className="w-4 h-4 text-destructive" /></Button>
                  : <Button size="icon" variant="ghost" title="Restore" data-testid={`feed-sup-restore-${s.supplier_id}`} onClick={() => act(() => api.post(`/feed/suppliers/${s.supplier_id}/restore`), "Restored")}><ArchiveRestore className="w-4 h-4 text-primary" /></Button>}
              </div>
            </td>
          </tr>
        ))}
      </FeedTable>
      <FeedFormDialog open={open} setOpen={setOpen} busy={busy} onSave={save} title={editing ? "Edit Supplier" : "Add Supplier"}
        fields={<>
          <FF label="Name"><Input className="am-input" data-testid="fs-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FF>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Business Name"><Input className="am-input" value={form.business_name || ""} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></FF>
            <FF label="Mobile"><Input className="am-input" data-testid="fs-mobile" value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></FF>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Supplier Type">
              <Select value={form.supplier_type} onValueChange={(v) => setForm({ ...form, supplier_type: v })}>
                <SelectTrigger className="am-input" data-testid="fs-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PROCUREMENT">Procurement (we buy from them)</SelectItem>
                  <SelectItem value="MARKETPLACE">Marketplace (they sell via us)</SelectItem>
                  <SelectItem value="BOTH">Both</SelectItem>
                </SelectContent>
              </Select>
            </FF>
            <FF label="Commission %"><Input className="am-input" type="number" min={0} max={100} data-testid="fs-commission" value={form.commission_percent ?? ""} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} /></FF>
          </div>
          <FF label="Areas (comma separated)"><Input className="am-input" data-testid="fs-areas" value={form.areas_text || ""} onChange={(e) => setForm({ ...form, areas_text: e.target.value })} /></FF>
          <FF label="Payment Details (UPI / bank)"><Input className="am-input" data-testid="fs-payment" value={form.payment_details || ""} onChange={(e) => setForm({ ...form, payment_details: e.target.value })} /></FF>
        </>} />
    </div>
  );
}

/* ---------- PURCHASES TAB (Model A procurement) ---------- */
function PurchasesTab({ products, suppliers, refresh }) {
  const [purchases, setPurchases] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [expenses, setExpenses] = useState([]);
  const [busy, setBusy] = useState(false);
  const directProducts = products.filter((p) => p.supply_model === "DIRECT" && !p.is_archived);
  const procSuppliers = suppliers.filter((s) => !s.is_archived && s.supplier_type !== "MARKETPLACE");

  const load = useCallback(() => {
    api.get("/feed/purchases").then((r) => setPurchases(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const refreshAll = () => { load(); refresh(); };

  const expTotal = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const totalCost = (Number(form.qty) || 0) * (Number(form.unit_cost) || 0) + expTotal;

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/feed/purchases", {
        supplier_id: form.supplier_id, product_id: form.product_id,
        qty: Number(form.qty), unit_cost: Number(form.unit_cost),
        expenses: expenses.filter((e) => e.label && Number(e.amount) > 0).map((e) => ({ label: e.label, amount: Number(e.amount) })),
        notes: form.notes || "",
      });
      toast.success("Purchase order created");
      setOpen(false); refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refreshAll(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  return (
    <div className="space-y-4">
      <Button className="am-btn-primary rounded-full" data-testid="feed-add-purchase-btn" onClick={() => { setForm({}); setExpenses([]); setOpen(true); }}>
        <Plus className="w-4 h-4 mr-1" /> New Purchase
      </Button>
      <FeedTable testId="feed-purchases-table" cols={["Purchase", "Product", "Qty", "Unit Cost", "Expenses", "Total Cost", "Landed/Unit", "Status", "Actions"]}>
        {purchases.map((p) => (
          <tr key={p.purchase_id} className="border-b last:border-0" data-testid={`feed-purchase-row-${p.purchase_id}`}>
            <td className="px-3 py-3 whitespace-nowrap"><div className="font-bold">{p.supplier_name}</div>
              <div className="text-xs text-muted-foreground font-mono">{p.purchase_id.slice(0, 8)} · {(p.created_at || "").slice(0, 10)}</div></td>
            <td className="px-3 py-3 whitespace-nowrap">{p.product_name}</td>
            <td className="px-3 py-3 whitespace-nowrap">{p.qty} {p.unit}</td>
            <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(p.unit_cost)}</td>
            <td className="px-3 py-3 whitespace-nowrap text-xs">{(p.expenses || []).map((e) => `${e.label} ${fmtMoney(e.amount)}`).join(", ") || "—"}</td>
            <td className="px-3 py-3 whitespace-nowrap font-bold">{fmtMoney(p.total_cost)}</td>
            <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(p.landed_unit_cost)}</td>
            <td className="px-3 py-3"><FeedBadge status={p.status} /></td>
            <td className="px-3 py-3">
              {p.status === "ORDERED" && (
                <div className="flex gap-1.5 justify-end">
                  <Button size="sm" className="am-btn-primary rounded-full text-xs" data-testid={`feed-receive-${p.purchase_id}`}
                    onClick={() => act(() => api.post(`/feed/purchases/${p.purchase_id}/receive`), "Stock received")}>
                    <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Receive
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full text-xs text-destructive"
                    onClick={() => act(() => api.post(`/feed/purchases/${p.purchase_id}/cancel`), "Purchase cancelled")}>Cancel</Button>
                </div>
              )}
            </td>
          </tr>
        ))}
        {purchases.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-muted-foreground">No purchases yet. Procure stock for AnimalMela Direct products here.</td></tr>}
      </FeedTable>

      <FeedFormDialog open={open} setOpen={setOpen} busy={busy} onSave={save} wide title="New Purchase (AnimalMela procurement)"
        fields={<>
          <FF label="Procurement Supplier">
            <Select value={form.supplier_id || ""} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
              <SelectTrigger className="am-input" data-testid="fpu-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
              <SelectContent>{procSuppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </FF>
          <FF label="Product (AnimalMela Direct only)">
            <Select value={form.product_id || ""} onValueChange={(v) => setForm({ ...form, product_id: v })}>
              <SelectTrigger className="am-input" data-testid="fpu-product"><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent>{directProducts.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name} (stock {p.stock_qty})</SelectItem>)}</SelectContent>
            </Select>
          </FF>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Quantity"><Input className="am-input" type="number" min={0} data-testid="fpu-qty" value={form.qty || ""} onChange={(e) => setForm({ ...form, qty: e.target.value })} /></FF>
            <FF label="Unit Cost ₹"><Input className="am-input" type="number" min={0} data-testid="fpu-cost" value={form.unit_cost || ""} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></FF>
          </div>
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Expenses (transport, labour…)</div>
            {expenses.map((e, i) => (
              <div key={i} className="flex gap-2">
                <Input className="am-input h-9 text-sm flex-1" placeholder="Label" data-testid={`fpu-exp-label-${i}`} value={e.label}
                  onChange={(ev) => setExpenses(expenses.map((x, j) => (j === i ? { ...x, label: ev.target.value } : x)))} />
                <Input className="am-input h-9 text-sm w-28" type="number" min={0} placeholder="₹" data-testid={`fpu-exp-amount-${i}`} value={e.amount}
                  onChange={(ev) => setExpenses(expenses.map((x, j) => (j === i ? { ...x, amount: ev.target.value } : x)))} />
                <Button size="icon" variant="ghost" onClick={() => setExpenses(expenses.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid="fpu-add-expense" onClick={() => setExpenses([...expenses, { label: "", amount: "" }])}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Expense
            </Button>
          </div>
          <div className="rounded-xl bg-muted px-4 py-2.5 text-sm font-bold flex justify-between">
            <span>Total landed cost</span><span data-testid="fpu-total">{fmtMoney(totalCost)}</span>
          </div>
        </>} />
    </div>
  );
}

/* ---------- SETTLEMENTS TAB (Model B payouts) ---------- */
function SettlementsTab() {
  const [rows, setRows] = useState([]);
  const [fin, setFin] = useState(null);
  const [paidFor, setPaidFor] = useState(null);
  const [txnRef, setTxnRef] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => {
    api.get("/feed/settlements").then((r) => setRows(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
    api.get("/feed/finance/summary").then((r) => setFin(r.data)).catch(() => {});
  };
  useEffect(refresh, []);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const kpis = fin && [
    ["Marketplace GMV", fmtMoney(fin.marketplace.gmv)],
    ["Commission Earned", fmtMoney(fin.marketplace.commission_earned)],
    ["Supplier Payable", fmtMoney(fin.marketplace.supplier_payable)],
    ["Pending", `${fin.marketplace.settlements_pending.count} · ${fmtMoney(fin.marketplace.settlements_pending.amount)}`],
    ["Paid", `${fin.marketplace.settlements_paid.count} · ${fmtMoney(fin.marketplace.settlements_paid.amount)}`],
  ];
  return (
    <div className="space-y-4">
      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="feed-settle-kpis">
          {kpis.map(([label, value]) => (
            <div key={label} className="am-card p-4">
              <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
              <div className="font-display text-lg font-extrabold mt-1">{value}</div>
            </div>
          ))}
        </div>
      )}
      <FeedTable testId="feed-settlements-table" cols={["Settlement", "Supplier", "Amount", "Commission", "Payable", "Status", "Actions"]}>
        {rows.map((s) => (
          <tr key={s.settlement_id} className="border-b last:border-0" data-testid={`feed-settle-row-${s.settlement_id}`}>
            <td className="px-3 py-3 whitespace-nowrap"><div className="font-mono text-xs">{s.settlement_id.slice(0, 8)}</div>
              <div className="text-xs text-muted-foreground">order {s.order_id.slice(0, 8)} · {(s.delivered_at || "").slice(0, 10)}</div></td>
            <td className="px-3 py-3 font-bold whitespace-nowrap">{s.supplier_name}</td>
            <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(s.amount)}</td>
            <td className="px-3 py-3 whitespace-nowrap text-emerald-700 font-semibold">{fmtMoney(s.commission)}</td>
            <td className="px-3 py-3 whitespace-nowrap font-bold" data-testid={`feed-settle-payable-${s.settlement_id}`}>{fmtMoney(s.payable)}</td>
            <td className="px-3 py-3"><FeedBadge status={s.status} />
              {s.transaction_reference && <div className="text-[11px] text-muted-foreground mt-0.5">{s.transaction_reference}</div>}</td>
            <td className="px-3 py-3">
              <div className="flex gap-1.5 justify-end">
                {s.status === "PENDING" && (
                  <Button size="sm" variant="outline" className="rounded-full text-xs" disabled={busy}
                    data-testid={`feed-settle-approve-${s.settlement_id}`}
                    onClick={() => act(() => api.post(`/feed/settlements/${s.settlement_id}/approve`), "Settlement approved")}>Approve</Button>
                )}
                {s.status === "APPROVED" && (
                  <Button size="sm" className="am-btn-primary rounded-full text-xs" disabled={busy}
                    data-testid={`feed-settle-pay-${s.settlement_id}`} onClick={() => { setPaidFor(s); setTxnRef(""); }}>Mark Paid</Button>
                )}
              </div>
            </td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-muted-foreground">Settlements appear automatically when marketplace orders are delivered.</td></tr>}
      </FeedTable>
      <FeedFormDialog open={!!paidFor} setOpen={() => setPaidFor(null)} busy={busy || !txnRef.trim()}
        onSave={() => act(async () => {
          await api.post(`/feed/settlements/${paidFor.settlement_id}/mark-paid`, { transaction_reference: txnRef });
          setPaidFor(null);
        }, "Settlement paid")}
        title={`Pay ${paidFor?.supplier_name} — ${fmtMoney(paidFor?.payable)}`}
        fields={<FF label="Transaction Reference *"><Input className="am-input" data-testid="feed-settle-txn" value={txnRef} onChange={(e) => setTxnRef(e.target.value)} /></FF>} />
    </div>
  );
}

/* ---------- REPORTS TAB ---------- */
const REPORT_TYPES = [["daily", "Daily"], ["monthly", "Monthly"], ["product", "By Product"], ["supplier", "By Seller"], ["model", "By Business Model"], ["category", "By Category"]];

function ReportsTab() {
  const [groupBy, setGroupBy] = useState("daily");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/feed/reports", { params: { group_by: groupBy } }).then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load report"));
  }, [groupBy]);
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {REPORT_TYPES.map(([v, l]) => (
          <TabBtn key={v} testId={`feed-report-${v}`} active={groupBy === v} onClick={() => setGroupBy(v)}>{l}</TabBtn>
        ))}
      </div>
      <FeedTable testId="feed-reports-table" cols={["Key", "Lines", "Qty", "Revenue", "COGS", "Margin (Direct)", "Commission (Marketplace)"]}>
        {(data?.rows || []).map((r) => (
          <tr key={r.key} className="border-b last:border-0">
            <td className="px-3 py-3 font-bold whitespace-nowrap">{r.key}</td>
            <td className="px-3 py-3">{r.lines}</td>
            <td className="px-3 py-3">{r.qty}</td>
            <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(r.revenue)}</td>
            <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(r.cogs)}</td>
            <td className="px-3 py-3 whitespace-nowrap font-semibold text-emerald-700">{fmtMoney(r.margin)}</td>
            <td className="px-3 py-3 whitespace-nowrap font-semibold text-violet-700">{fmtMoney(r.commission)}</td>
          </tr>
        ))}
        {data && data.rows.length > 0 && (
          <tr className="font-bold bg-muted/50" data-testid="feed-report-totals">
            <td className="px-3 py-3">TOTAL</td>
            <td className="px-3 py-3">{data.totals.lines}</td>
            <td className="px-3 py-3">{data.totals.qty}</td>
            <td className="px-3 py-3">{fmtMoney(data.totals.revenue)}</td>
            <td className="px-3 py-3">{fmtMoney(data.totals.cogs)}</td>
            <td className="px-3 py-3 text-emerald-700">{fmtMoney(data.totals.margin)}</td>
            <td className="px-3 py-3 text-violet-700">{fmtMoney(data.totals.commission)}</td>
          </tr>
        )}
        {data && data.rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-muted-foreground">No delivered orders in range yet.</td></tr>}
      </FeedTable>
    </div>
  );
}

/* ---------- PAGE ---------- */
export default function AdminFeed() {
  const [tab, setTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const refresh = useCallback(() => {
    api.get("/feed/products", { params: { include_archived: true } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/feed/suppliers", { params: { include_archived: true } }).then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return (
    <DashboardShell title="Feed & Fodder" subtitle="AnimalMela direct supply + third-party supplier marketplace">
      <div className="flex gap-2 flex-wrap mb-6">
        <TabBtn testId="feed-tab-dashboard" active={tab === "dashboard"} onClick={() => setTab("dashboard")} icon={LayoutDashboard}>Dashboard</TabBtn>
        <TabBtn testId="feed-tab-products" active={tab === "products"} onClick={() => setTab("products")} icon={Wheat}>Products</TabBtn>
        <TabBtn testId="feed-tab-suppliers" active={tab === "suppliers"} onClick={() => setTab("suppliers")} icon={Store}>Suppliers</TabBtn>
        <TabBtn testId="feed-tab-purchases" active={tab === "purchases"} onClick={() => setTab("purchases")} icon={PackagePlus}>Purchases & Stock</TabBtn>
        <TabBtn testId="feed-tab-orders" active={tab === "orders"} onClick={() => setTab("orders")} icon={ShoppingCart}>Orders</TabBtn>
        <TabBtn testId="feed-tab-settlements" active={tab === "settlements"} onClick={() => setTab("settlements")} icon={Wallet}>Settlements</TabBtn>
        <TabBtn testId="feed-tab-reports" active={tab === "reports"} onClick={() => setTab("reports")} icon={BarChart3}>Reports</TabBtn>
      </div>
      {tab === "dashboard" && <DashboardTab />}
      {tab === "products" && <ProductsTab products={products} suppliers={suppliers} refresh={refresh} />}
      {tab === "suppliers" && <SuppliersTab suppliers={suppliers} refresh={refresh} />}
      {tab === "purchases" && <PurchasesTab products={products} suppliers={suppliers} refresh={refresh} />}
      {tab === "orders" && <OrdersPanel isAdmin onMutate={refresh} />}
      {tab === "settlements" && <SettlementsTab />}
      {tab === "reports" && <ReportsTab />}
    </DashboardShell>
  );
}
